/**
 * Standard Ride Booking Page - Enhanced
 * Features:
 * - Public access for estimates (no login required)
 * - Login required only for booking
 * - Book for another person
 * - Saved locations (pickup/dropoff) for quick select
 * - Preset places (hospitals, malls, airports, schools, etc.)
 * - Save location checkboxes for future bookings
 * - Generate share location links for pickup and dropoff
 * - Uses shared AddressInput component (same as Package flow)
 * - Isolated from package system - DOES NOT TOUCH GPS SETTINGS
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, MapPin, Navigation, Car, AlertCircle, LogIn, 
  User, Users, Heart, Building2, GraduationCap, ShoppingBag, 
  Plane, Hospital, ChevronDown, Star, Save, Check, Share2, Copy, Link,
  Mail, MessageCircle, Loader2 as Loader2Icon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import VehicleSelector from './components/VehicleSelector';
import AddressInput from '../../components/shared/AddressInput';
import RideFareEstimate from './components/RideFareEstimate';
import RideMapPreview from './components/RideMapPreview';
import RidePayment from './components/RidePayment';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get max passengers per vehicle type
const getMaxPassengers = (vehicleType) => {
  const limits = {
    economy: 4,
    standard: 4,
    premium: 4,
    suv: 6,
    van: 10
  };
  return limits[vehicleType] || 4;
};

// Category icons
const CATEGORY_ICONS = {
  hospitals: Hospital,
  shopping: ShoppingBag,
  transport: Plane,
  universities: GraduationCap,
  areas: MapPin,
  attractions: Star,
  government: Building2,
  banks: Building2,
  supermarkets: ShoppingBag
};

const StandardRide = () => {
  const navigate = useNavigate();
  const { token, user, isAuthenticated } = useAuth();
  
  // Form state - using same structure as package flow
  const [pickup, setPickup] = useState({
    street: '',
    city: '',
    parish: 'Kingston',
    coordinates: null
  });
  const [dropoff, setDropoff] = useState({
    street: '',
    city: '',
    parish: 'Kingston',
    coordinates: null
  });
  const [vehicleType, setVehicleType] = useState('standard');
  const [paymentMethod, setPaymentMethod] = useState('fygaro');
  const [passengerCount, setPassengerCount] = useState(1);
  
  // Book for another person
  const [bookForAnother, setBookForAnother] = useState(false);
  const [riderName, setRiderName] = useState('');
  const [riderPhone, setRiderPhone] = useState('');
  
  // Saved locations
  const [savedPickupLocations, setSavedPickupLocations] = useState([]);
  const [savedDropoffLocations, setSavedDropoffLocations] = useState([]);
  const [savePickupAddress, setSavePickupAddress] = useState(false);
  const [saveDropoffAddress, setSaveDropoffAddress] = useState(false);
  
  // Preset places
  const [presetCategories, setPresetCategories] = useState([]);
  const [presetLocations, setPresetLocations] = useState([]);
  const [showPresetPicker, setShowPresetPicker] = useState(null); // 'pickup' or 'dropoff'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [presetSearchQuery, setPresetSearchQuery] = useState('');
  
  // API state
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Share location links
  const [pickupShareLink, setPickupShareLink] = useState(null);
  const [dropoffShareLink, setDropoffShareLink] = useState(null);
  const [generatingPickupLink, setGeneratingPickupLink] = useState(false);
  const [generatingDropoffLink, setGeneratingDropoffLink] = useState(false);

  // Fetch saved locations on mount
  useEffect(() => {
    const fetchSavedLocations = async () => {
      if (!isAuthenticated || !token) return;
      try {
        const response = await fetch(`${API_URL}/api/locations/saved`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.locations) {
          setSavedPickupLocations(data.locations.filter(l => l.type === 'pickup'));
          setSavedDropoffLocations(data.locations.filter(l => l.type === 'delivery' || l.type === 'dropoff'));
        }
      } catch (error) {
        console.log('No saved locations');
      }
    };
    fetchSavedLocations();
  }, [isAuthenticated, token]);

  // Fetch preset categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/locations/categories`);
        const data = await response.json();
        if (data.categories) {
          setPresetCategories(data.categories);
        }
      } catch (error) {
        console.log('Failed to fetch preset categories');
      }
    };
    fetchCategories();
  }, []);

  // Restore pending booking after login
  useEffect(() => {
    // Small delay to ensure localStorage is available after hard redirect
    const timer = setTimeout(() => {
      const pending = localStorage.getItem('pendingRideBooking');
      console.log('[RIDE] Checking pendingRideBooking:', pending ? 'FOUND' : 'NOT FOUND');
      if (pending) {
        try {
          const state = JSON.parse(pending);
          console.log('[RIDE] Restoring state:', { pickup: !!state.pickup, dropoff: !!state.dropoff });
          if (state.pickup) setPickup(state.pickup);
          if (state.dropoff) setDropoff(state.dropoff);
          if (state.vehicleType) setVehicleType(state.vehicleType);
          if (state.passengerCount) setPassengerCount(state.passengerCount);
          if (state.bookForAnother) setBookForAnother(state.bookForAnother);
          if (state.riderName) setRiderName(state.riderName);
          if (state.riderPhone) setRiderPhone(state.riderPhone);
          localStorage.removeItem('pendingRideBooking');
          toast.success('Your ride details have been restored!');
        } catch (e) {
          console.error('Failed to restore pending booking:', e);
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Auto-fetch estimate when locations change
  useEffect(() => {
    if (pickup?.coordinates && dropoff?.coordinates) {
      fetchEstimate();
    } else {
      setEstimate(null);
    }
  }, [pickup?.coordinates, dropoff?.coordinates, vehicleType]);

  // Fetch preset locations by category
  const fetchPresetsByCategory = async (categoryKey) => {
    try {
      const response = await fetch(`${API_URL}/api/locations/category/${categoryKey}`);
      const data = await response.json();
      if (data.locations) {
        setPresetLocations(data.locations);
      }
    } catch (error) {
      console.log('Failed to fetch preset locations');
    }
  };

  // Search preset locations
  const searchPresets = async (query) => {
    if (query.length < 2) return;
    try {
      const response = await fetch(`${API_URL}/api/locations/search?q=${encodeURIComponent(query)}&limit=15`);
      const data = await response.json();
      if (data.results) {
        setPresetLocations(data.results);
      }
    } catch (error) {
      console.log('Failed to search presets');
    }
  };

  // Apply saved pickup location
  const applySavedPickup = (loc) => {
    if (loc.address) {
      setPickup({
        street: loc.address.street || '',
        city: loc.address.city || '',
        parish: loc.address.parish || 'Kingston',
        coordinates: loc.address.coordinates || null,
        fullAddress: `${loc.address.street}, ${loc.address.city}, ${loc.address.parish}`
      });
      toast.success('Pickup location applied');
    }
  };

  // Apply saved dropoff location
  const applySavedDropoff = (loc) => {
    if (loc.address) {
      setDropoff({
        street: loc.address.street || '',
        city: loc.address.city || '',
        parish: loc.address.parish || 'Kingston',
        coordinates: loc.address.coordinates || null,
        fullAddress: `${loc.address.street}, ${loc.address.city}, ${loc.address.parish}`
      });
      toast.success('Dropoff location applied');
    }
  };

  // Apply preset location
  const applyPresetLocation = (loc, type) => {
    const locationData = {
      street: loc.street || loc.name,
      city: loc.town || loc.city || '',
      parish: loc.parish || 'Kingston',
      coordinates: loc.coordinates,
      fullAddress: loc.display_address || `${loc.name}, ${loc.town || ''}, ${loc.parish || ''}`
    };
    
    if (type === 'pickup') {
      setPickup(locationData);
    } else {
      setDropoff(locationData);
    }
    
    setShowPresetPicker(null);
    setSelectedCategory(null);
    setPresetSearchQuery('');
    toast.success(`${loc.name} selected as ${type}`);
  };

  const fetchEstimate = async () => {
    if (!pickup?.coordinates || !dropoff?.coordinates) return;
    
    setEstimateLoading(true);
    setEstimateError(null);
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      
      const response = await fetch(`${API_URL}/api/rides/estimate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          pickup: {
            address: pickup.fullAddress || `${pickup.street}, ${pickup.city}, ${pickup.parish}`,
            coordinates: pickup.coordinates,
            parish: pickup.parish || 'Kingston',
            city: pickup.city || pickup.parish || 'Kingston'
          },
          dropoff: {
            address: dropoff.fullAddress || `${dropoff.street}, ${dropoff.city}, ${dropoff.parish}`,
            coordinates: dropoff.coordinates,
            parish: dropoff.parish || 'Kingston',
            city: dropoff.city || dropoff.parish || 'Kingston'
          },
          vehicle_type: vehicleType,
          is_tourist: false,
          priority_bonus: 0
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setEstimate(data.estimate);
      } else {
        setEstimateError(data.detail || data.error?.message || 'Failed to get estimate');
      }
    } catch (err) {
      console.error('Estimate error:', err);
      setEstimateError('Network error. Please check your connection.');
    } finally {
      setEstimateLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    const rideState = {
      pickup,
      dropoff,
      vehicleType,
      passengerCount,
      bookForAnother,
      riderName,
      riderPhone,
      returnTo: '/ride/standard'
    };
    localStorage.setItem('pendingRideBooking', JSON.stringify(rideState));
    console.log('[RIDE] Saved pendingRideBooking to localStorage, keys:', Object.keys(rideState));
    navigate('/login?redirect=/ride/standard');
  };

  // Generate share location link for pickup
  const generatePickupShareLink = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to generate share links');
      return;
    }
    setGeneratingPickupLink(true);
    try {
      const response = await fetch(`${API_URL}/api/ride-locations/request-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_name: bookForAnother && riderName ? riderName : 'Passenger',
          recipient_phone: bookForAnother && riderPhone ? riderPhone : '',
          recipient_email: '',
          location_type: 'pickup',
          ride_notes: 'Please share your pickup location for the ride'
        })
      });
      const data = await response.json();
      if (data.success && data.share_url) {
        setPickupShareLink(data.share_url);
        toast.success('Pickup share link generated!');
      } else {
        toast.error(data.detail || 'Failed to generate link');
      }
    } catch (error) {
      console.error('Error generating pickup link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setGeneratingPickupLink(false);
    }
  };

  // Generate share location link for dropoff
  const generateDropoffShareLink = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to generate share links');
      return;
    }
    setGeneratingDropoffLink(true);
    try {
      const response = await fetch(`${API_URL}/api/ride-locations/request-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_name: bookForAnother && riderName ? riderName : 'Passenger',
          recipient_phone: bookForAnother && riderPhone ? riderPhone : '',
          recipient_email: '',
          location_type: 'destination',
          ride_notes: 'Please share your destination location for the ride'
        })
      });
      const data = await response.json();
      if (data.success && data.share_url) {
        setDropoffShareLink(data.share_url);
        toast.success('Destination share link generated!');
      } else {
        toast.error(data.detail || 'Failed to generate link');
      }
    } catch (error) {
      console.error('Error generating dropoff link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setGeneratingDropoffLink(false);
    }
  };

  // Copy link to clipboard
  const copyShareLink = async (link, type) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`${type} share link copied!`);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(`${type} share link copied!`);
    }
  };

  // Share via WhatsApp
  const shareViaWhatsApp = (link, type) => {
    const recipientName = bookForAnother && riderName ? riderName : 'there';
    const msg = encodeURIComponent(`Hi ${recipientName}! I'm booking a ride for you. Please share your ${type.toLowerCase()} location by clicking this link: ${link}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  // Share via Email
  const shareViaEmail = (link, type) => {
    const recipientName = bookForAnother && riderName ? riderName : 'there';
    const subject = encodeURIComponent(`Share Your ${type} Location - GO Ride`);
    const body = encodeURIComponent(`Hi ${recipientName},\n\nI'm booking a ride for you and need your ${type.toLowerCase()} location.\n\nPlease click this link to share your location:\n${link}\n\nThank you!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Share link via native share (mobile)
  const shareLink = async (link, type) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Share Your ${type} Location`,
          text: `Click this link to share your ${type.toLowerCase()} location for the ride`,
          url: link
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyShareLink(link, type);
        }
      }
    } else {
      copyShareLink(link, type);
    }
  };

  // Save locations after successful booking
  const saveLocationsAfterBooking = async (rideId) => {
    if (!savePickupAddress && !saveDropoffAddress) return;
    
    try {
      if (savePickupAddress && pickup.street) {
        await fetch(`${API_URL}/api/locations/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'pickup',
            label: `${pickup.city || pickup.parish} - ${pickup.street.substring(0, 30)}`,
            street: pickup.street,
            city: pickup.city,
            parish: pickup.parish,
            lat: pickup.coordinates?.lat,
            lng: pickup.coordinates?.lng
          })
        });
      }
      
      if (saveDropoffAddress && dropoff.street) {
        await fetch(`${API_URL}/api/locations/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'delivery',
            label: `${dropoff.city || dropoff.parish} - ${dropoff.street.substring(0, 30)}`,
            street: dropoff.street,
            city: dropoff.city,
            parish: dropoff.parish,
            lat: dropoff.coordinates?.lat,
            lng: dropoff.coordinates?.lng
          })
        });
      }
      
      toast.success('Locations saved for quick select!');
    } catch (error) {
      console.error('Failed to save locations:', error);
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      handleLoginRedirect();
      return;
    }

    if (!pickup?.coordinates || !dropoff?.coordinates || !estimate) {
      setSubmitError('Please select pickup and destination with GPS coordinates');
      return;
    }

    if (!pickup.street) {
      setSubmitError('Please enter a pickup street address');
      return;
    }

    if (!dropoff.street) {
      setSubmitError('Please enter a destination street address');
      return;
    }

    if (bookForAnother && (!riderName || !riderPhone)) {
      setSubmitError('Please enter rider name and phone number');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const requestBody = {
        pickup: {
          address: pickup.fullAddress || `${pickup.street}, ${pickup.city}, ${pickup.parish}`,
          street: pickup.street,
          city: pickup.city,
          coordinates: pickup.coordinates,
          parish: pickup.parish || 'Kingston'
        },
        dropoff: {
          address: dropoff.fullAddress || `${dropoff.street}, ${dropoff.city}, ${dropoff.parish}`,
          street: dropoff.street,
          city: dropoff.city,
          coordinates: dropoff.coordinates,
          parish: dropoff.parish || 'Kingston'
        },
        vehicle_type: vehicleType,
        payment_method: paymentMethod,
        ride_type: 'on_demand',
        passenger_count: passengerCount
      };

      // Add booking for another person info
      if (bookForAnother) {
        requestBody.book_for_another = true;
        requestBody.rider_name = riderName;
        requestBody.rider_phone = riderPhone;
      }

      const response = await fetch(`${API_URL}/api/rides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Save locations if checkboxes were checked
        await saveLocationsAfterBooking(data.ride?._id);
        
        toast.success('Ride booked! Looking for a driver...');
        navigate('/ride/track/' + data.ride._id);
      } else {
        setSubmitError(data.detail || 'Failed to book ride');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate USD fare
  const fareUSD = estimate && typeof estimate.estimated_fare === 'number' ? (estimate.estimated_fare / 150).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-bold text-gray-900">Book a Ride</h1>
            <p className="text-xs text-gray-500">Standard on-demand service</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        
        {/* Book for Another Person Toggle */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="font-medium text-gray-800">Book for someone else?</p>
                <p className="text-xs text-gray-500">Enter their details for the driver</p>
              </div>
            </div>
            <Checkbox
              checked={bookForAnother}
              onCheckedChange={setBookForAnother}
              data-testid="book-for-another-checkbox"
            />
          </div>
          
          {bookForAnother && (
            <div className="mt-4 pt-4 border-t space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Rider's Name</label>
                <Input
                  value={riderName}
                  onChange={(e) => setRiderName(e.target.value)}
                  placeholder="Enter passenger name"
                  className="mt-1"
                  data-testid="rider-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Rider's Phone</label>
                <Input
                  value={riderPhone}
                  onChange={(e) => setRiderPhone(e.target.value)}
                  placeholder="Enter passenger phone"
                  className="mt-1"
                  data-testid="rider-phone-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Location inputs section */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          
          {/* Saved Pickup Locations (for logged in users) */}
          {isAuthenticated && savedPickupLocations.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Quick Select Pickup</label>
              <select
                className="w-full p-2 border rounded-lg text-sm bg-emerald-50 border-emerald-200"
                onChange={(e) => {
                  const loc = savedPickupLocations.find(l => l.id === e.target.value);
                  if (loc) applySavedPickup(loc);
                }}
                defaultValue=""
                data-testid="saved-pickup-select"
              >
                <option value="">-- Select saved pickup --</option>
                {savedPickupLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Preset Places Button for Pickup */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPresetPicker('pickup')}
            className="w-full border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="preset-pickup-btn"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Choose from Popular Places
          </Button>

          <AddressInput
            label="Pickup Location"
            value={pickup}
            onChange={setPickup}
            placeholder="e.g., 3 Regal Ave Horizon Park Spanish Town"
            showGpsButton={true}
            theme="light"
            testIdPrefix="pickup"
          />
          
          {/* Save pickup checkbox */}
          {isAuthenticated && pickup.street && (
            <div className="flex items-center gap-2 ml-1">
              <Checkbox
                id="save-pickup"
                checked={savePickupAddress}
                onCheckedChange={setSavePickupAddress}
                data-testid="save-pickup-checkbox"
              />
              <label htmlFor="save-pickup" className="text-xs text-gray-600 cursor-pointer">
                Save this pickup for quick select
              </label>
            </div>
          )}
          
          {/* Share Pickup Location Link */}
          {isAuthenticated && (
            <div className="mt-2">
              {!pickupShareLink ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generatePickupShareLink}
                  disabled={generatingPickupLink}
                  className="w-full text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="generate-pickup-link-btn"
                >
                  <Link className="w-3 h-3 mr-2" />
                  {generatingPickupLink ? 'Generating...' : 'Generate Share Link for Pickup'}
                </Button>
              ) : (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                  <p className="text-xs text-amber-700 font-medium">Share this link for pickup location:</p>
                  <div className="flex gap-2">
                    <Input
                      value={pickupShareLink}
                      readOnly
                      className="text-xs h-8 bg-white flex-1"
                      data-testid="pickup-share-link-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyShareLink(pickupShareLink, 'Pickup')}
                      className="h-8 px-2 border-gray-300"
                      title="Copy link"
                      data-testid="copy-pickup-link-btn"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  {/* WhatsApp, Email buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => shareViaWhatsApp(pickupShareLink, 'Pickup')}
                      className="flex-1 h-8 bg-green-500 hover:bg-green-600 text-white text-xs"
                      data-testid="whatsapp-pickup-btn"
                    >
                      <MessageCircle className="w-3 h-3 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => shareViaEmail(pickupShareLink, 'Pickup')}
                      className="flex-1 h-8 border-amber-400 text-amber-700 hover:bg-amber-100 text-xs"
                      data-testid="email-pickup-btn"
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Email
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="border-l-2 border-dashed border-emerald-300 h-4 ml-5" />
          
          {/* Saved Dropoff Locations (for logged in users) */}
          {isAuthenticated && savedDropoffLocations.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Quick Select Destination</label>
              <select
                className="w-full p-2 border rounded-lg text-sm bg-blue-50 border-blue-200"
                onChange={(e) => {
                  const loc = savedDropoffLocations.find(l => l.id === e.target.value);
                  if (loc) applySavedDropoff(loc);
                }}
                defaultValue=""
                data-testid="saved-dropoff-select"
              >
                <option value="">-- Select saved destination --</option>
                {savedDropoffLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Preset Places Button for Dropoff */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPresetPicker('dropoff')}
            className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="preset-dropoff-btn"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Choose from Popular Places
          </Button>
          
          <AddressInput
            label="Destination"
            value={dropoff}
            onChange={setDropoff}
            placeholder="e.g., 31 King Street Downtown Kingston"
            showGpsButton={true}
            theme="light"
            testIdPrefix="dropoff"
          />

          {/* Save dropoff checkbox */}
          {isAuthenticated && dropoff.street && (
            <div className="flex items-center gap-2 ml-1">
              <Checkbox
                id="save-dropoff"
                checked={saveDropoffAddress}
                onCheckedChange={setSaveDropoffAddress}
                data-testid="save-dropoff-checkbox"
              />
              <label htmlFor="save-dropoff" className="text-xs text-gray-600 cursor-pointer">
                Save this destination for quick select
              </label>
            </div>
          )}
          
          {/* Share Dropoff Location Link */}
          {isAuthenticated && (
            <div className="mt-2">
              {!dropoffShareLink ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateDropoffShareLink}
                  disabled={generatingDropoffLink}
                  className="w-full text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="generate-dropoff-link-btn"
                >
                  <Link className="w-3 h-3 mr-2" />
                  {generatingDropoffLink ? 'Generating...' : 'Generate Share Link for Destination'}
                </Button>
              ) : (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                  <p className="text-xs text-blue-700 font-medium">Share this link for destination:</p>
                  <div className="flex gap-2">
                    <Input
                      value={dropoffShareLink}
                      readOnly
                      className="text-xs h-8 bg-white flex-1"
                      data-testid="dropoff-share-link-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyShareLink(dropoffShareLink, 'Destination')}
                      className="h-8 px-2 border-gray-300"
                      title="Copy link"
                      data-testid="copy-dropoff-link-btn"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  {/* WhatsApp, Email buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => shareViaWhatsApp(dropoffShareLink, 'Destination')}
                      className="flex-1 h-8 bg-green-500 hover:bg-green-600 text-white text-xs"
                      data-testid="whatsapp-dropoff-btn"
                    >
                      <MessageCircle className="w-3 h-3 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => shareViaEmail(dropoffShareLink, 'Destination')}
                      className="flex-1 h-8 border-blue-400 text-blue-700 hover:bg-blue-100 text-xs"
                      data-testid="email-dropoff-btn"
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Email
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preset Places Modal */}
        {showPresetPicker && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b sticky top-0 bg-white z-10">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900">
                    Select {showPresetPicker === 'pickup' ? 'Pickup' : 'Destination'}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowPresetPicker(null);
                      setSelectedCategory(null);
                      setPresetSearchQuery('');
                    }}
                  >
                    ✕
                  </Button>
                </div>
                
                {/* Search */}
                <Input
                  placeholder="Search hospitals, malls, airports..."
                  value={presetSearchQuery}
                  onChange={(e) => {
                    setPresetSearchQuery(e.target.value);
                    if (e.target.value.length >= 2) {
                      searchPresets(e.target.value);
                      setSelectedCategory('search');
                    } else {
                      setSelectedCategory(null);
                    }
                  }}
                  className="mb-2"
                  data-testid="preset-search-input"
                />
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {!selectedCategory ? (
                  // Category list
                  <div className="grid grid-cols-2 gap-2">
                    {presetCategories.map(cat => {
                      const Icon = CATEGORY_ICONS[cat.key] || MapPin;
                      return (
                        <button
                          key={cat.key}
                          onClick={() => {
                            setSelectedCategory(cat.key);
                            fetchPresetsByCategory(cat.key);
                          }}
                          className="p-3 rounded-xl border hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left"
                          data-testid={`category-${cat.key}`}
                        >
                          <Icon className="w-5 h-5 text-emerald-600 mb-1" />
                          <p className="font-medium text-sm text-gray-800">{cat.label}</p>
                          <p className="text-xs text-gray-500">{cat.count} places</p>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // Location list
                  <div className="space-y-2">
                    {selectedCategory !== 'search' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedCategory(null)}
                        className="mb-2"
                      >
                        ← Back to categories
                      </Button>
                    )}
                    
                    {presetLocations.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">Loading...</p>
                    ) : (
                      presetLocations.map(loc => (
                        <button
                          key={loc.id}
                          onClick={() => applyPresetLocation(loc, showPresetPicker)}
                          className="w-full p-3 rounded-xl border hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left"
                          data-testid={`preset-loc-${loc.id}`}
                        >
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium text-gray-800">{loc.name}</p>
                            {(loc.verified_location || loc.source === "pudo_verified") && (
                              <span data-testid="verified-location-badge" className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">Verified Location</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{loc.display_address || (loc.parish && `${loc.name}, ${loc.parish}`) || ""}</p>
                          {loc.category_label && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded mt-1 inline-block">
                              {loc.category_label}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Interactive Route Map */}
        <RideMapPreview
          pickup={pickup}
          dropoff={dropoff}
          onPickupDrag={(loc) => {
            setPickup(prev => ({ ...prev, ...loc }));
            toast.success('Pickup pin moved — ' + (loc.street || 'location updated'));
          }}
          onDropoffDrag={(loc) => {
            setDropoff(prev => ({ ...prev, ...loc }));
            toast.success('Destination pin moved — ' + (loc.street || 'location updated'));
          }}
          height={320}
        />

        {/* Vehicle selection */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Car className="w-5 h-5 text-emerald-600" />
            Choose Vehicle
          </h3>
          <VehicleSelector
            selected={vehicleType}
            onSelect={setVehicleType}
            disabled={estimateLoading}
          />
          
          {/* Passenger count */}
          <div className="mt-4 pt-4 border-t">
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Passengers: {passengerCount}
            </label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPassengerCount(Math.max(1, passengerCount - 1))}
                disabled={passengerCount <= 1}
              >
                -
              </Button>
              <span className="w-8 text-center font-semibold">{passengerCount}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPassengerCount(Math.min(getMaxPassengers(vehicleType), passengerCount + 1))}
                disabled={passengerCount >= getMaxPassengers(vehicleType)}
              >
                +
              </Button>
              <span className="text-xs text-gray-500 ml-2">
                Max {getMaxPassengers(vehicleType)} for {vehicleType}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Fare is per trip, not per passenger (within vehicle capacity)
            </p>
          </div>
        </div>

        {/* Fare estimate */}
        <RideFareEstimate
          estimate={estimate}
          loading={estimateLoading}
          error={estimateError}
          onRetry={fetchEstimate}
        />



        {/* Login prompt or payment selection */}
        {estimate && !isAuthenticated && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <LogIn className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-800 font-medium">Sign in to book</p>
                <p className="text-amber-700 text-sm mt-1">
                  Your fare estimate is J${estimate.estimated_fare || estimate.fare_jmd}. 
                  Sign in to complete your booking.
                </p>
                <Button
                  onClick={handleLoginRedirect}
                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="login-to-book-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign in to Book
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Payment selection (only for authenticated users) */}
        {estimate && isAuthenticated && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <RidePayment
              selected={paymentMethod}
              onSelect={setPaymentMethod}
              fareJMD={estimate.estimated_fare || estimate.fare_jmd}
              fareUSD={fareUSD}
              onConfirm={handleSubmit}
              loading={submitting}
              disabled={!pickup?.coordinates || !dropoff?.coordinates || !estimate}
            />
          </div>
        )}

        {/* Error display */}
        {submitError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium">Booking Failed</p>
              <p className="text-red-600 text-sm">{submitError}</p>
            </div>
          </div>
        )}

        {/* Get estimate button (when no estimate yet) */}
        {!estimate && pickup?.street && dropoff?.street && (
          <Button
            onClick={fetchEstimate}
            disabled={estimateLoading || !pickup?.coordinates || !dropoff?.coordinates}
            className="w-full py-6 text-lg bg-emerald-600 hover:bg-emerald-700"
            data-testid="get-estimate-btn"
          >
            {estimateLoading ? 'Getting estimate...' : 
             (!pickup?.coordinates || !dropoff?.coordinates) ? 'Set GPS for both locations' : 'Get Fare Estimate'}
          </Button>
        )}

        {/* Help text */}
        {(!pickup?.coordinates || !dropoff?.coordinates) && (pickup?.street || dropoff?.street) && (
          <p className="text-xs text-center text-gray-500">
            Type an address and click "Find GPS" or select from suggestions to set coordinates for distance calculation.
          </p>
        )}
      </div>
    </div>
  );
};

export default StandardRide;
